/**
 * Member Profile Page
 * Detailed view of a single member
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Edit, User, Mail, Phone, MapPin, Calendar } from 'lucide-react';

import Button from '../../../components/common/Button/Button';
import { fetchMemberById } from '../memberSlice';
import { ROUTES, routeHelpers } from '../../../constants/routes';
import { formatDate } from '../../../utils/formatDate';
import { formatCurrency } from '../../../utils/formatCurrency';
import { usePermissions } from '../../../hooks/usePermissions';
import PERMISSIONS from '../../../constants/permissions';

const MemberProfile = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedMember, isLoading } = useSelector((state) => state.members);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    dispatch(fetchMemberById(id));
  }, [dispatch, id]);

  if (isLoading || !selectedMember) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const canEdit = hasPermission(PERMISSIONS.MEMBERS_EDIT);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(ROUTES.MEMBERS.LIST)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {selectedMember.firstName} {selectedMember.lastName}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Member ID: {selectedMember.memberId}
            </p>
          </div>
        </div>
        {canEdit && (
          <Button
            variant="primary"
            onClick={() => navigate(routeHelpers.memberEdit(id))}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Member
          </Button>
        )}
      </div>

      {/* Profile Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Full Name</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedMember.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedMember.phone}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Joined</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatDate(selectedMember.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Address
            </h2>
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedMember.address || 'No address provided'}
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  {selectedMember.city}{selectedMember.city && selectedMember.region ? ', ' : ''}{selectedMember.region}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Financial Summary
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Savings Balance</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(selectedMember.savingsBalance || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Shares Value</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(selectedMember.sharesValue || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Loan Balance</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(selectedMember.loanBalance || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Status
            </h2>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                selectedMember.status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {selectedMember.status || 'Active'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberProfile;
